import { sortBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isMeetingPending } from 'common-data-handling';

/**
 * Returns an array of meeting requests (i.e., meetings where the user is invited) for a given event, optionally filtered and sorted.
 *
 * @param {number} eventId - The ID of the event.
 * @param {Object} options - An optional object containing filter and sortBy functions.
 * @param {function} options.filter - A function that filters the meeting requests array.
 * @param {function} options.sortBy - A function that sorts the meeting requests array.
 * @return {Array} - An array of meeting requests for the given event, optionally filtered and sorted.
 */
export function useMeetingRequests(eventId, options = {}) {
    const { count, filter, sortBy: sortFunction } = options,
        selectRequestedMeetings = useCallback((state) => state.events[eventId]?.meetings?.invited, [eventId]),
        meetings = useSelector(selectRequestedMeetings);

    return useMemo(() => {
        let result = meetings.filter(isMeetingPending);

        if (filter) result = result.filter(filter);
        if (sortFunction) result = sortBy(result, sortFunction);
        if (count) result = result.slice(0, count);

        return result;
    }, [meetings, count, filter, sortFunction]);
}
